import React, { FC, useState } from "react";
import { Card, Form, Button, Input, Checkbox } from "antd";
import styled from "styled-components";
import { Store } from "antd/lib/form/interface";
import { useRegisterMutation } from "../../queries/auto-generated/hooks";
import _ from "lodash";
import { ViewTypeUpdater } from ".";
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import * as Sentry from "@sentry/react";

export const userNamePattern = /^[0-9a-zA-Z]{5,24}$/;
export const passwordMin = 8;
export const passwordMax = 36;

const Register: FC<{ setType: ViewTypeUpdater }> = ({ setType }) => {
  const [form] = Form.useForm();
  const [registerMutation] = useRegisterMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedAtLeastOnce, setHasSubmittedAtLeastOnce] = useState<
    boolean
  >(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [showCompletionMessage, setShowCompletionMessage] = useState<boolean>(
    false
  );
  const [validatedEmail, setValidatedEmail] = useState<string | null>(null);

  const validateTrigger = hasSubmittedAtLeastOnce
    ? ["onChange", "onSubmit"]
    : "onSubmit";

  const handleButtonClick = () => {
    setIsSubmitting(true);
    setErrorMessages([]);
    setHasSubmittedAtLeastOnce(true);
    form.submit();
  };

  const handleFinish = (values: Store) => {
    const { email, password, firstName, lastName, phoneNumber } = values;
    registerMutation({
      variables: {
        input: {
          username: email,
          email,
          password,
          firstName,
          lastName,
          phoneNumber,
        },
      },
    })
      .then((response) => {
        console.log({ response });
        setValidatedEmail(email);
        setShowCompletionMessage(true);
      })
      .catch((e) => {
        console.error(e);
        const errorId = _.get(
          e,
          "graphQLErrors[0].extensions.exception.data.message[0].messages[0].id"
        );
        if (
          errorId === "Auth.form.error.email.taken" ||
          errorId === "Auth.form.error.username.taken"
        ) {
          setErrorMessages(["Email already taken"]);
        } else {
          setErrorMessages(["Trouble communicating with the server : ("]);
          Sentry.captureMessage("Problem registering user");
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleFinishedFailed = () => {
    setIsSubmitting(false);
  };

  const ErrorMessages: FC = () => {
    if (!errorMessages || errorMessages.length === 0) {
      return null;
    }

    return (
      <StyledList>
        {errorMessages.map((message) => (
          <li>{message}</li>
        ))}
      </StyledList>
    );
  };

  if (showCompletionMessage) {
    const message = `Please confirm your email address by clicking on the link we just sent
    to your inbox at ${validatedEmail}.`;

    return <StyledMessageCard>{message}</StyledMessageCard>;
  }

  const primaryAction = (
    <Button
      size="large"
      type="primary"
      shape="round"
      block
      onClick={handleButtonClick}
      loading={isSubmitting}
    >
      Create account
    </Button>
  );

  return (
    <Card title="Create an account">
      <Form
        form={form}
        name="register"
        scrollToFirstError
        colon={false}
        hideRequiredMark
        onFinish={handleFinish}
        onFinishFailed={handleFinishedFailed}
        validateTrigger={validateTrigger}
      >
        <StyledItem
          name="firstName"
          rules={[
            {
              required: true,
              message: "Please input your first name",
            },
          ]}
        >
          <Input placeholder="First name" prefix={<UserOutlined />} />
        </StyledItem>
        <StyledItem
          name="lastName"
          rules={[
            {
              required: true,
              message: "Please input your last name",
            },
          ]}
        >
          <Input placeholder="Last name" prefix={<UserOutlined />} />
        </StyledItem>
        <StyledItem
          name="email"
          rules={[
            {
              required: true,
              message: "Please input your email address",
              type: "email",
            },
          ]}
        >
          <Input
            placeholder="Email"
            prefix={<MailOutlined className={"input-prefix"} />}
          />
        </StyledItem>
        <StyledItem
          name="phoneNumber"
          rules={[
            { required: true, message: "Please enter your phone number" },
          ]}
        >
          <Input placeholder="Phone number" prefix={<PhoneOutlined />} />
        </StyledItem>
        <StyledItem
          name="password"
          rules={[
            {
              required: true,
              message: "Please input your password",
            },

            {
              message: `Password must be between ${passwordMin} and ${passwordMax} characters long`,
              min: passwordMin,
              max: passwordMax,
            },
          ]}
        >
          <Input.Password
            placeholder="Password"
            prefix={<LockOutlined className={"input-prefix"} />}
          />
        </StyledItem>

        <StyledItem
          name="passwordConfirmation"
          dependencies={["password"]}
          rules={[
            {
              required: true,
              message: "Please repeat your password",
            },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject("Passwords do not match");
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Confirm password"
            prefix={<LockOutlined className={"input-prefix"} />}
          />
        </StyledItem>

        <StyledItem
          name="agree"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      "You need to accept the privacy policy before creating an account"
                    ),
            },
          ]}
        >
          <StyledCheckbox>
            Agree to{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://jaybirdschicken.com/terms/"
            >
              our terms and conditions
            </a>
          </StyledCheckbox>
        </StyledItem>

        <ErrorMessages />
        {primaryAction}
        <AlternativeOptions>
          <Button type="text" shape="round" onClick={() => setType(undefined)}>
            Back
          </Button>
        </AlternativeOptions>
      </Form>
    </Card>
  );
};

const StyledItem = styled(Form.Item)`
  display: block;
`;

const StyledList = styled.ul`
  list-style: none;
  color: #a61d24;
  padding-left: 0;
`;

const StyledMessageCard = styled(Card)`
  text-align: center;
`;

const StyledCheckbox = styled(Checkbox)``;

const AlternativeOptions = styled.div`
  padding-top: 1em;
  text-align: center;
`;

export default Register;
